"use client";
var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/primitive.tsx
import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import { gsap as gsap2 } from "gsap";
import ScrollTrigger from "gsap/dist/ScrollTrigger";
import * as Portal from "@radix-ui/react-portal";

// src/util/build-declarative-tween.ts
import { gsap } from "gsap";
function buildDeclarativeTween(_a) {
  var _b = _a, {
    op,
    id,
    target,
    duration,
    paused
  } = _b, timelineAndPosition = __objRest(_b, [
    "op",
    "id",
    "target",
    "duration",
    "paused"
  ]);
  if ("to" in op) {
    if (timelineAndPosition.timeline) {
      timelineAndPosition.timeline.to(
        target,
        __spreadProps(__spreadValues({}, op.to), { id, duration, paused }),
        timelineAndPosition.position
      );
    } else {
      gsap.to(target, __spreadProps(__spreadValues({}, op.to), { id, duration, paused }));
    }
  } else if ("from" in op) {
    if (timelineAndPosition.timeline) {
      timelineAndPosition.timeline.from(
        target,
        __spreadProps(__spreadValues({}, op.from), { id, duration, paused }),
        timelineAndPosition.position
      );
    } else {
      gsap.from(target, __spreadProps(__spreadValues({}, op.from), { id, duration, paused }));
    }
  } else if ("fromTo" in op) {
    if (timelineAndPosition.timeline) {
      timelineAndPosition.timeline.fromTo(
        target,
        __spreadValues({}, op.fromTo[0]),
        __spreadProps(__spreadValues({}, op.fromTo[1]), { id, duration, paused }),
        timelineAndPosition.position
      );
    } else {
      gsap.fromTo(
        target,
        __spreadValues({}, op.fromTo[0]),
        __spreadProps(__spreadValues({}, op.fromTo[1]), { id, duration })
      );
    }
  } else
    throw new Error("Invalid TweenOp");
  return () => {
    var _a2;
    (_a2 = gsap.getById(id)) == null ? void 0 : _a2.revert();
  };
}

// src/hooks/use-iso-layout-effect.ts
import { useEffect, useLayoutEffect } from "react";
var useIsoLayoutEffect = typeof document === "undefined" ? useEffect : useLayoutEffect;

// src/primitive.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var ScrollytellingContext = React.createContext(void 0);
var useScrollytelling = () => {
  const scrollytelling = React.useContext(ScrollytellingContext);
  if (!scrollytelling) {
    throw new Error(
      "useScrollytelling must be used within a Scrollytelling.Root"
    );
  }
  return scrollytelling;
};
var ScrollytellingDispatchersContext = React.createContext(void 0);
var useDispatcher = () => {
  const dispatcher = React.useContext(ScrollytellingDispatchersContext);
  if (!dispatcher) {
    throw new Error("useDispatcher must be used within a ScrollytellingRoot");
  }
  return dispatcher;
};
var Scrollytelling = ({
  children,
  debug,
  start,
  end,
  callbacks,
  scrub,
  defaults,
  toggleActions
}) => {
  const ref = React.useRef(null);
  const scopedQuerySelector = gsap2.utils.selector(ref);
  const id = React.useId();
  const restTweenId = `rest-${id}`;
  const [timeline, setTimeline] = React.useState();
  React.useEffect(() => {
    if (!ref.current)
      return;
    gsap2.registerPlugin(ScrollTrigger);
    const tl = gsap2.timeline({
      scrollTrigger: __spreadValues({
        markers: debug,
        scrub: scrub != null ? scrub : true,
        start: start != null ? start : "top top",
        end: end != null ? end : "bottom bottom",
        trigger: ref.current,
        toggleActions
      }, callbacks),
      paused: true,
      defaults: __spreadProps(__spreadValues({}, defaults), { duration: 1 })
    });
    setTimeline(tl);
    return () => {
      tl.revert();
    };
  }, [end, debug, start, callbacks, scrub, defaults, toggleActions]);
  const addRestToTimeline = React.useCallback(
    (lastEnd, timeline2) => {
      var _a;
      (_a = gsap2.getById(restTweenId)) == null ? void 0 : _a.revert();
      const duration = 100 - lastEnd;
      const position = 100 - duration;
      timeline2.to({}, { id: restTweenId, duration }, position);
      return () => {
        var _a2;
        (_a2 = gsap2.getById(restTweenId)) == null ? void 0 : _a2.revert();
      };
    },
    [restTweenId]
  );
  const getTimelineSpace = React.useCallback(
    ({ start: start2, end: end2 }) => {
      if (!timeline)
        throw new Error("timeline not initialized");
      const duration = end2 - start2;
      if (start2 < 0) {
        throw new Error("start time must be greater than 0");
      }
      if (end2 > 100) {
        throw new Error("end time must be less than 100");
      }
      if (duration < 0) {
        throw new Error("end time must be greater than start time");
      }
      const cleanup = addRestToTimeline(end2, timeline);
      return {
        duration,
        position: start2,
        cleanup: () => {
          cleanup();
        }
      };
    },
    [addRestToTimeline, timeline]
  );
  return /* @__PURE__ */ jsx(ScrollytellingContext.Provider, { value: { timeline, rootRef: ref }, children: /* @__PURE__ */ jsxs(
    ScrollytellingDispatchersContext.Provider,
    {
      value: { getTimelineSpace, scopedQuerySelector },
      children: [
        /* @__PURE__ */ jsx(Slot, { ref, children }),
        debug && /* @__PURE__ */ jsx(Portal.Root, { container: ref.current, asChild: true, children: /* @__PURE__ */ jsx(
          "div",
          {
            style: { position: "absolute", top: 0, right: 0, height: "100%" },
            children: /* @__PURE__ */ jsx("div", { style: { position: "sticky", top: 0 }, children: /* @__PURE__ */ jsx(Debugger, {}) })
          }
        ) })
      ]
    }
  ) });
};
var Debugger = () => {
  const { timeline, rootRef } = useScrollytelling();
  const [progress, setProgress] = React.useState();
  React.useEffect(() => {
    if (!timeline || !rootRef.current)
      return;
    const handleUpdate = () => {
      const progress2 = timeline.progress();
      if (!timeline.scrollTrigger)
        return;
      const start = timeline.scrollTrigger.start;
      const end = timeline.scrollTrigger.end;
      const scroll = (end - start) * progress2;
      setProgress({
        percentage: `${(progress2 * 100).toFixed(2)}%`,
        px: `${scroll.toFixed(0)}px`
      });
    };
    timeline.eventCallback("onUpdate", handleUpdate);
    return () => {
      timeline.eventCallback("onUpdate", null);
    };
  }, [timeline, rootRef]);
  return /* @__PURE__ */ jsx(
    "div",
    {
      style: {
        width: "370px",
        padding: "24px",
        border: "1px solid black",
        fontSize: "12px",
        background: "#360202"
      },
      children: /* @__PURE__ */ jsx("pre", { children: JSON.stringify({ progress }, null, 2) })
    }
  );
};
function Animation(props) {
  const ref = React.useRef(null);
  const id = React.useId();
  const { timeline } = useScrollytelling();
  const { getTimelineSpace } = useDispatcher();
  React.useEffect(() => {
    if (!timeline || !props.tween)
      return;
    const addTweenToTimeline = (tween) => {
      const tweenTarget = getTweenTarget({
        targetContainer: "target" in tween ? tween : {},
        ref
      });
      if (tweenTarget) {
        const timelineSpace = getTimelineSpace({
          start: tween.start,
          end: tween.end
        });
        const cleanup = buildDeclarativeTween({
          id,
          timeline,
          op: tween,
          target: tweenTarget,
          duration: timelineSpace.duration,
          position: timelineSpace.position
        });
        return () => {
          cleanup();
          timelineSpace.cleanup();
        };
      } else
        return () => void 0;
    };
    if (Array.isArray(props.tween)) {
      const cleanupTweens = props.tween.map((tween) => {
        const cleanup = addTweenToTimeline(tween);
        return cleanup;
      });
      return () => {
        cleanupTweens.forEach((cleanup) => cleanup());
      };
    } else {
      const cleanup = addTweenToTimeline(props.tween);
      return () => {
        cleanup();
      };
    }
  }, [getTimelineSpace, id, props.tween, timeline]);
  if (props.children) {
    return /* @__PURE__ */ jsx(Slot, { ref, children: props.children });
  }
  return null;
}
function Waypoint(props) {
  const ref = React.useRef(null);
  const id = React.useId();
  const lastStateRef = React.useRef(
    "idle"
  );
  const { timeline } = useScrollytelling();
  React.useEffect(() => {
    if (!timeline)
      return;
    let cleanupTween = void 0;
    let generatedTween = void 0;
    if (props.tween) {
      const _a = props.tween, { duration } = _a, op = __objRest(_a, ["duration"]);
      const tweenTarget = getTweenTarget({ targetContainer: props.tween, ref });
      cleanupTween = buildDeclarativeTween({
        id: id + "-tween",
        op,
        duration,
        target: tweenTarget,
        paused: true
      });
      generatedTween = gsap2.getById(id + "-tween");
    }
    const validAt = getValidAt(props.at);
    const newSet = gsap2.set({}, { id, paused: true });
    if (lastStateRef.current === "complete") {
      newSet.play();
    }
    newSet.vars.onComplete = () => {
      var _a2;
      lastStateRef.current = "complete";
      (_a2 = props.onCall) == null ? void 0 : _a2.call(props);
      generatedTween == null ? void 0 : generatedTween.play();
    };
    newSet.vars.onReverseComplete = () => {
      var _a2, _b;
      lastStateRef.current = "reverse-complete";
      (_a2 = props.onReverseCall) == null ? void 0 : _a2.call(props);
      if (!((_b = props.tween) == null ? void 0 : _b.forwards)) {
        generatedTween == null ? void 0 : generatedTween.reverse();
      }
    };
    timeline.add(newSet, validAt);
    newSet.play();
    if (props.label) {
      timeline.addLabel(props.label, validAt);
    }
    return () => {
      var _a2;
      (_a2 = gsap2.getById(id)) == null ? void 0 : _a2.revert();
      cleanupTween == null ? void 0 : cleanupTween();
      if (props.label)
        timeline.removeLabel(props.label);
    };
  }, [id, timeline, props]);
  if (props.children) {
    return /* @__PURE__ */ jsx(Slot, { ref, children: props.children });
  }
  return null;
}
function getValidAt(at) {
  if (at < 0) {
    throw new Error("at must be a positive number");
  }
  if (at > 100) {
    throw new Error("at must be less than 100");
  }
  if (at === 0) {
    return 1e-6;
  }
  if (at === 100) {
    return 99.999999;
  }
  return at;
}
function Parallax({
  children,
  tween
}) {
  return /* @__PURE__ */ jsx(
    Animation,
    {
      tween: __spreadProps(__spreadValues({}, tween), {
        fromTo: [
          {
            y: tween.movementY ? -1 * tween.movementY.value + tween.movementY.unit : void 0,
            x: tween.movementX ? -1 * tween.movementX.value + tween.movementX.unit : void 0
          },
          {
            y: tween.movementY ? tween.movementY.value + tween.movementY.unit : void 0,
            x: tween.movementX ? tween.movementX.value + tween.movementX.unit : void 0,
            ease: "linear"
          }
        ]
      }),
      children
    }
  );
}
var Pin = React.forwardRef(
  ({
    children,
    top = 0,
    childHeight,
    pinSpacerHeight,
    pinSpacerClassName,
    childClassName
  }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        style: { height: pinSpacerHeight },
        ref,
        className: pinSpacerClassName,
        children: /* @__PURE__ */ jsx(
          "div",
          {
            style: { position: "sticky", top, height: childHeight },
            className: childClassName,
            children
          }
        )
      }
    );
  }
);
Pin.displayName = "Pin";
var RegisterGsapPlugins = ({ plugins }) => {
  useIsoLayoutEffect(() => {
    gsap2.registerPlugin(...plugins);
  }, []);
  return null;
};
var useScrollToLabel = (label, opts) => {
  const scrollytelling = useScrollytelling();
  return React.useCallback(() => {
    var _a;
    if (!scrollytelling.timeline)
      throw new Error("timeline not initialized");
    const st = scrollytelling.timeline.scrollTrigger;
    if (!st)
      throw new Error("scrollTrigger not found");
    const foundLabel = scrollytelling.timeline.labels[label];
    if (!foundLabel)
      throw new Error(`label ${label} not found`);
    const targetPx = st.labelToScroll(label);
    window.scrollTo({
      top: targetPx + ((_a = opts == null ? void 0 : opts.offset) != null ? _a : 0),
      behavior: opts == null ? void 0 : opts.behavior
    });
  }, [label, opts == null ? void 0 : opts.behavior, opts == null ? void 0 : opts.offset, scrollytelling.timeline]);
};
function getTweenTarget({
  targetContainer,
  ref
}) {
  if (targetContainer.target) {
    if (targetContainer.target && typeof targetContainer.target === "object" && "current" in targetContainer.target) {
      return targetContainer.target.current;
    } else {
      return targetContainer.target;
    }
  } else if (ref) {
    return ref.current;
  } else {
    return null;
  }
}

// src/image-sequence-canvas.tsx
import * as React2 from "react";

// src/util/merge-refs.ts
function mergeRefs(refs) {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(value);
      } else if (ref != null) {
        ref.current = value;
      }
    });
  };
}

// src/image-sequence-canvas.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var ImageSequenceCanvas = React2.forwardRef((_a, ref) => {
  var _b = _a, { controllerRef, getFrameSrc } = _b, rest = __objRest(_b, ["controllerRef", "getFrameSrc"]);
  const canvasRef = React2.useRef(null);
  const supportTableRef = React2.useRef({
    supportsAvif: false,
    supportsWebp: false
  });
  const supportTablePromiseRef = React2.useRef(null);
  React2.useEffect(() => {
    supportTablePromiseRef.current = Promise.all([
      webpIsSupported(),
      avifIsSupported()
    ]).then(([supportsWebp, supportsAvif]) => {
      supportTableRef.current = { supportsWebp, supportsAvif };
    });
  }, []);
  React2.useImperativeHandle(
    controllerRef,
    () => {
      return {
        preload: (frameStart, frameEnd) => __async(void 0, null, function* () {
          yield supportTablePromiseRef.current;
          const promises = [];
          for (let frame = frameStart; frame <= frameEnd; frame++) {
            const src = getFrameSrc(frame, supportTableRef.current);
            promises.push(loadImage(src));
          }
          yield Promise.all(promises);
        }),
        draw: (frame) => __async(void 0, null, function* () {
          yield supportTablePromiseRef.current;
          const src = getFrameSrc(frame, supportTableRef.current);
          const canvas = canvasRef.current;
          const context = canvas == null ? void 0 : canvas.getContext("2d");
          if (!canvas || !context || !src)
            return;
          const img = yield loadImage(src);
          context.mozImageSmoothingEnabled = false;
          context.webkitImageSmoothingEnabled = false;
          context.msImageSmoothingEnabled = false;
          context.imageSmoothingEnabled = false;
          context.clearRect(0, 0, canvas.width, canvas.height);
          context.drawImage(img, 0, 0, canvas.width, canvas.height);
        }),
        get canvas() {
          return canvasRef.current;
        }
      };
    },
    [getFrameSrc]
  );
  return /* @__PURE__ */ jsx2("canvas", __spreadProps(__spreadValues({}, rest), { ref: mergeRefs([canvasRef, ref]) }));
});
ImageSequenceCanvas.displayName = "Scrollytelling.ImageSequenceCanvas";
var imagesLoadedCache = /* @__PURE__ */ new Map();
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const imageFromCache = imagesLoadedCache.get(src);
    if (imageFromCache) {
      if (imageFromCache.complete) {
        resolve(imageFromCache);
      } else {
        imageFromCache.addEventListener("load", () => resolve(imageFromCache), {
          once: true
        });
      }
    } else {
      const image = new Image();
      image.addEventListener("load", () => resolve(image), { once: true });
      image.onerror = () => reject(new Error(`Failed to load image: ${src}`));
      image.src = src;
      imagesLoadedCache.set(src, image);
    }
  });
}
function webpIsSupported() {
  return __async(this, null, function* () {
    if (!window.createImageBitmap)
      return false;
    const webpData = "data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoCAAEAAQAcJaQAA3AA/v3AgAA=";
    const blob = yield fetch(webpData).then((r) => r.blob());
    return createImageBitmap(blob).then(
      () => true,
      () => false
    );
  });
}
function avifIsSupported() {
  return __async(this, null, function* () {
    if (!window.createImageBitmap)
      return false;
    const avifData = "data:image/avif;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoCAAEAAQAcJaQAA3AA/v3AgAA=";
    const blob = yield fetch(avifData).then((r) => r.blob());
    return createImageBitmap(blob).then(
      () => true,
      () => false
    );
  });
}
export {
  Animation,
  ImageSequenceCanvas,
  Parallax,
  Pin,
  RegisterGsapPlugins,
  Scrollytelling as Root,
  Waypoint,
  useScrollToLabel,
  useScrollytelling
};
